/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getAppInsightLogger } from 'src/core/services/telemetry/applicationInsightsLogger';
import { getConsoleLogger } from 'src/core/services/telemetry/consoleLogger';
import { LogData, Logger } from 'src/core/services/telemetry/logger';
import { RootStore } from 'src/core/stores/RootStore';
import { UIConfigsStore } from 'src/core/stores/UIConfigsStore';
import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { noop } from 'src/core/utils/base';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { getUserSessionId } from 'src/core/utils/userSessionId';
/**
 * A logger that provides both logging methods and logging of timing.
 */
export type TelemetryLogger = Logger & {
    createTimedActivityLogger?: <T = void>(
        name: string,
        getLogData?: (result: T, error: Error) => LogData
    ) => (fn: Promise<T>) => Promise<T>;
};

// Aggregates a collection of loggers into a telemetry logger
const aggregateLoggers = (loggers: Logger[]): TelemetryLogger => {
    const info = (message: string, properties?: LogData) => {
        loggers.forEach(logger => logger.info(message, properties));
    };

    const warn = (message: string, properties?: LogData) => {
        loggers.forEach(logger => logger.warn(message, properties));
    };

    const error = (exception: Error, properties: LogData) => {
        loggers.forEach(logger => logger.error(exception, properties));
    };

    const logEvent = (name: string, properties?: LogData) => {
        loggers.forEach(logger => logger.logEvent(name, properties));
    };

    const startEvent = (name: string, properties?: LogData) => {
        loggers.forEach(logger => logger.startEvent(name, properties));
    };

    const endEvent = (name: string, failed: boolean, properties?: LogData) => {
        loggers.forEach(logger => logger.endEvent(name, failed, properties));
    };

    const logPageView = (name: string) => {
        loggers.forEach(logger => logger.logPageView(name));
    };

    const createTimedActivityLogger = <T>(name: string, getLogData?: (result?: T, error?: Error) => LogData) => async (
        promise: Promise<T>
    ) => {
        try {
            const result = await promise;
            const successLog = getLogData ? getLogData(result, null) : undefined;
            startEvent(name, successLog);
            endEvent(name, false, successLog);

            return result;
        } catch (e) {
            const failureLog = getLogData(null, <Error>e);
            error(<Error>e, failureLog);
            endEvent(name, true, failureLog);
            throw e;
        }
    };

    return {
        info,
        warn,
        error,
        logEvent,
        startEvent,
        endEvent,
        logPageView,
        createTimedActivityLogger
    };
};

// Adds properties to each call of a logger
const addPropsToLogger = (logger: TelemetryLogger, addProperties: LogData | (() => LogData)): TelemetryLogger => {
    const getProperties = typeof addProperties === 'function' ? addProperties : () => addProperties;

    const info = (message: string, properties?: LogData) => {
        logger.info(message, { ...getProperties(), ...properties });
    };

    const warn = (message: string, properties?: LogData) => {
        logger.warn(message, { ...getProperties(), ...properties });
    };

    const error = (exception: Error, properties: LogData) => {
        logger.error(exception, { ...getProperties(), ...properties });
    };

    const logEvent = (name: string, properties?: LogData) => {
        logger.logEvent(name, { ...getProperties(), ...properties });
    };

    const startEvent = (name: string, properties?: LogData) => {
        logger.startEvent(name, { ...getProperties(), ...properties });
    };

    const endEvent = (name: string, failed: boolean, properties?: LogData) => {
        logger.endEvent(name, failed, { ...getProperties(), ...properties });
    };

    const createTimedActivityLogger = <T>(name: string, getLogData?: (result?: T, error?: Error) => LogData) => async (
        promise: Promise<T>
    ) => {
        try {
            logger.startEvent(name);
            const result = await promise;
            const successLog = getLogData ? getLogData(result, null) : undefined;
            endEvent(name, false, successLog);

            return result;
        } catch (e) {
            const failureLog = getLogData ? getLogData(null, <Error>e) : undefined;
            error(<Error>e, failureLog);
            endEvent(name, true, failureLog);
            throw e;
        }
    };

    return {
        info,
        warn,
        error,
        logEvent,
        startEvent,
        endEvent,
        logPageView: logger.logPageView,
        createTimedActivityLogger
    };
};

// Creates a telemetry logger
const createLogger = (properties: LogData | (() => LogData)): TelemetryLogger => {
    let logger: TelemetryLogger;
    if (isDevelopmentEnvironment()) {
        logger = aggregateLoggers([getConsoleLogger(), getAppInsightLogger()]);
    } else {
        logger = aggregateLoggers([getAppInsightLogger()]);
    }

    return addPropsToLogger(logger, properties);
};

type TelemetryLoggerSingleton = {
    current: TelemetryLogger;
};

const noopLogger: TelemetryLogger = {
    info: noop,
    warn: noop,
    error: noop,
    logEvent: noop,
    startEvent: noop,
    endEvent: noop,
    logPageView: noop,
    createTimedActivityLogger: <T>() => async (promise: Promise<T>) => await promise
};

// The singleton logger defaults to a noop implementation to avoid
// errors when initializeRootLogger has not yet been called.
const theLogger: TelemetryLoggerSingleton = {
    current: noopLogger
};

/**
 * Initializes the singleton logger.
 * @param properties Properties to include with each logging call
 */
export const initializeLogger = (properties: LogData | (() => LogData)) => {
    theLogger.current = createLogger(properties);
};

/**
 * Initializes the singleton logger from a root store
 * @param rootStore The rootStore to use for contextual data included with each logging call.
 */
export const initializeLoggerFromRoot = (rootStore: RootStore, uiStore: UIConfigsStore) => {
    // A method is defined so the latest values are used in each call
    const getContextProps = () => ({
        currentDate: new Date(),
        userSessionId: getUserSessionId(),
        appId: rootStore.currentLuisAppStore?.id.toString(),
        csAccountInternalId: rootStore.azureStore.session.csAccountStore?.id,
        subscriptionId: rootStore.azureStore.session.subscriptionId,
        isSignedIn: rootStore.authStore.isSignedIn,
        portalKind: getPortalFromUrl().kind,
        // to be enabled when applying flighting on any feature
        // assignmentContext: rootStore.azureExpStore.assignmentContext,
        // impression_id: rootStore.azureExpStore.impressionId,
        market: uiStore.locale?.code,
        browser: rootStore.browserType
    });

    initializeLogger(getContextProps);
};

/**
 * Resets the singleton logger back to noop.
 */
export const unInitializeLogger = () => {
    theLogger.current = noopLogger;
};

/**
 * Gets the singleton root logger for the application.
 * @param activitySource Optional activity source to include with each logging call.
 */
export const getLogger = (activitySource?: string): TelemetryLogger => addPropsToLogger(theLogger.current, { activitySource });
